import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/**
 * Streama CMS · Lens store
 *
 * "Lens" preserves the prototype's perspective-switcher: Admin,
 * Content, Growth, Ops, Support. Switching a lens does NOT change
 * which screens exist (that's controlled by privileges); it changes
 * the ORDER of nav sections so the most-relevant ones surface first.
 *
 * Verified against streama-cms.html lines 7316-7321 (the LENS const)
 * — the `order` arrays are byte-identical.
 *
 * State persists to localStorage so admins keep their chosen lens
 * across sessions.
 */

const STORAGE_KEY = 'streama-cms.lens'

export type LensId = 'admin' | 'content' | 'growth' | 'ops' | 'support'

export interface Lens {
  id: LensId
  name: string
  label: string
  /**
   * Section IDs in display order. Section IDs come from the router's
   * SECTIONS constant (mirrors prototype's SECTIONS line 7301-7313).
   */
  order: string[]
}

/**
 * The five lenses. Four match the prototype verbatim; `support` is
 * added to surface the existing Cases/Refunds/Operator-portal screens
 * for support staff.
 */
const LENSES: Record<LensId, Lens> = {
  admin: {
    id: 'admin',
    name: 'Admin · Everything',
    label: 'FULL ACCESS',
    order: [
      'overview',
      'operations',
      'content',
      'people',
      'storefront',
      'audience',
      'support',
      'revenue',
      'partners',
      'messaging',
      'platform',
    ],
  },
  content: {
    id: 'content',
    name: 'Content & Merchandising',
    label: 'CONTENT',
    order: [
      'overview',
      'content',
      'people',
      'storefront',
      'partners',
      'messaging',
      'audience',
      'support',
      'revenue',
      'operations',
      'platform',
    ],
  },
  growth: {
    id: 'growth',
    name: 'Growth & Revenue',
    label: 'GROWTH',
    order: [
      'overview',
      'revenue',
      'partners',
      'audience',
      'support',
      'messaging',
      'storefront',
      'content',
      'people',
      'operations',
      'platform',
    ],
  },
  ops: {
    id: 'ops',
    name: 'Platform Ops',
    label: 'OPS',
    order: [
      'overview',
      'operations',
      'support',
      'audience',
      'platform',
      'storefront',
      'content',
      'people',
      'partners',
      'messaging',
      'revenue',
    ],
  },
  support: {
    id: 'support',
    name: 'Customer Support',
    label: 'SUPPORT',
    order: [
      'overview',
      'support',
      'audience',
      'revenue',
      'messaging',
      'content',
      'people',
      'storefront',
      'partners',
      'operations',
      'platform',
    ],
  },
}

export const useLensStore = defineStore('lens', () => {
  const currentId = ref<LensId>(loadInitial())

  const current = computed<Lens>(() => LENSES[currentId.value])

  const allLenses = computed<Lens[]>(() => Object.values(LENSES))

  function setLens(id: LensId): void {
    if (!LENSES[id]) return
    currentId.value = id
    localStorage.setItem(STORAGE_KEY, id)
  }

  function loadInitial(): LensId {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && stored in LENSES) {
      return stored as LensId
    }
    return 'admin'
  }

  return {
    currentId,
    current,
    allLenses,
    setLens,
  }
})
